import { useIntersectionObserver } from '@folklore/hooks';
import classNames from 'classnames';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from 'react-intl';

import usePlaceholderItems from '../../hooks/usePlaceholderItems';
import { useTimelineInfinite } from '../../hooks/useTimeline';
import useTransformTimelineItem from '../../hooks/useTransformTimelineItem';
import { getImageUrl } from '../../lib/getImageSize';

import { useBrands, useDefaultBrand } from '../../contexts/SiteContext';
import BasicButton from '../buttons/BasicButton';
import TimelineHorizontalCard from '../cards/TimelineHorizontalCard';
import SearchField from '../fields/SearchField';
import BrandLogo from '../icons/BrandLogo';
import CaretIcon from '../icons/CaretIcon';
import Accordion from '../partials/Accordion';
import Duration from '../typography/Duration';
import ItemCount from '../typography/ItemCount';
import ListTitle from '../typography/ListTitle';

import styles from '../../styles/panels/account-timeline-panel.module.css';

const propTypes = {
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

function AccountTimelinePanel({ className }) {
    const intl = useIntl();
    const [search, setSearch] = useState(null);

    const defaultBrand = useDefaultBrand();
    const brands = useBrands();

    const onSearchChange = useCallback(
        (newSearch) => {
            setSearch(newSearch);
        },
        [setSearch],
    );

    const {
        pages = null,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
    } = useTimelineInfinite(
        search !== null
            ? {
                  search,
                  has_related: true,
              }
            : {
                  has_related: true,
              },
        {
            enabled: false,
        },
    );

    const {
        ref: refEnd,
        entry: { isIntersecting: endInView = false },
    } = useIntersectionObserver({
        disabled: isFetching || isFetchingNextPage,
    });

    useEffect(() => {
        if (endInView && !isFetching && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [isFetching, isFetchingNextPage, endInView]);

    const placeholderItems = usePlaceholderItems(5);

    const transformTimelineItem = useTransformTimelineItem();

    const items = useMemo(() => {
        if (pages === null) {
            return [
                { key: 'placeholder', items: [{ brand: defaultBrand, items: placeholderItems }] },
            ];
        }

        return pages
            .reduce((allItems, { data: pageItems = [] }) => [...allItems, ...pageItems], [])
            .map((item) => transformTimelineItem(item))
            .filter((it) => it !== null)
            .reduce((groups, item) => {
                const { createdAt = null } = item || {};
                if (createdAt === null) {
                    return [...groups];
                }
                const dateObject = parseISO(createdAt);
                const dateKey = format(dateObject, 'yyyyMMdd') || null;

                const currentGroupIndex = groups.findIndex(({ key = null }) => key === dateKey);

                return currentGroupIndex !== -1
                    ? [
                          ...groups.slice(0, currentGroupIndex),
                          {
                              ...groups[currentGroupIndex],
                              items: [...groups[currentGroupIndex].items, item],
                          },
                          ...groups.slice(currentGroupIndex + 1),
                      ]
                    : [
                          ...groups,
                          {
                              key: dateKey,
                              date: dateObject,
                              items: [item],
                          },
                      ];
            }, [])
            .map(({ key, items: groupItems, ...group }) => {
                const groupedByBrand = groupItems.reduce((brandGroups, item) => {
                    const { brand } = item || {};
                    const { handle: brandHandle } = brand || {};

                    const currentBrandIndex = brandGroups.length - 1;
                    const currentBrandGroup =
                        currentBrandIndex > -1 ? brandGroups[currentBrandIndex] : null;
                    const isSameBrand =
                        currentBrandGroup !== null &&
                        currentBrandGroup.brand?.handle === brandHandle;
                    if (!isSameBrand) {
                        return [
                            ...brandGroups,
                            {
                                brand,
                                items: [item],
                            },
                        ];
                    }

                    const { document: lastDocument = null } =
                        brandGroups[currentBrandIndex].items[
                            brandGroups[currentBrandIndex].items.length - 1
                        ];
                    const { id: lastDocumentId = null } = lastDocument || {};
                    const { document = null } = item;
                    const { id: documentId = null } = document || {};

                    if (
                        documentId !== null &&
                        lastDocumentId !== null &&
                        documentId === lastDocumentId
                    ) {
                        return brandGroups;
                    }

                    return [
                        ...brandGroups.slice(0, currentBrandIndex),
                        {
                            ...brandGroups[currentBrandIndex],
                            items: [...brandGroups[currentBrandIndex].items, item],
                        },
                        ...brandGroups.slice(currentBrandIndex + 1),
                    ];
                }, []);

                return {
                    key,
                    items: groupedByBrand,
                    count: groupItems.length,
                    ...group,
                };
            });
    }, [pages, placeholderItems]);

    const [collapsed, setCollapsed] = useState([]);

    const onClickSectionButton = useCallback(
        (key) => {
            const currentKeyIndex = collapsed.findIndex((sectionKey) => sectionKey === key);

            const newCollapsed =
                currentKeyIndex !== -1
                    ? [
                          ...collapsed.slice(0, currentKeyIndex),
                          ...collapsed.slice(currentKeyIndex + 1),
                      ]
                    : [...collapsed, key];

            setCollapsed(newCollapsed);
        },
        [collapsed],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            {/* <SearchField
                value={search}
                placeholder={intl.formatMessage({
                    defaultMessage: "Rechercher dans l'historique...",
                    description: 'Field placeholder',
                })}
                loading={isFetching || isFetchingNextPage}
                onChange={onSearchChange}
                className={styles.field}
            /> */}
            <div className={styles.list}>
                {(items || []).map(({ key, date, count, items: dateItems = [] }) => {
                    const isCollapsed = collapsed.indexOf(key) !== -1;
                    return (
                        <div
                            key={key}
                            className={classNames([
                                styles.dateSection,
                                {
                                    [styles.collapsed]: isCollapsed,
                                },
                            ])}
                        >
                            <div className={styles.header}>
                                <BasicButton
                                    onClick={() => onClickSectionButton(key)}
                                    className={styles.dateButton}
                                >
                                    <ListTitle tag="span" className={styles.dateTitle}>
                                        <FormattedDate
                                            value={date}
                                            day="2-digit"
                                            month="short"
                                            x
                                            year="numeric"
                                        />
                                        <CaretIcon
                                            className={classNames([
                                                styles.caret,
                                                { [styles.rotate]: isCollapsed },
                                            ])}
                                        />
                                    </ListTitle>
                                    <ItemCount tag="span" className={styles.count}>
                                        <FormattedMessage
                                            defaultMessage="{count} {count, plural, one {item} other {items}}"
                                            description="List count"
                                            values={{ count }}
                                        />
                                    </ItemCount>
                                </BasicButton>
                            </div>
                            <Accordion
                                collapsed={isCollapsed}
                                duration={`${count > 5 ? count * 0.02 : count * 0.1}s`}
                            >
                                {(dateItems || []).map(({ brand, items: brandItems = [] }, i) => {
                                    const {
                                        handle: brandHandle = null,
                                        images,
                                        image,
                                    } = brands.find(
                                        ({ handle }) => brand !== null && brand.handle === handle,
                                    ) || defaultBrand;
                                    const { logo } = images || {};
                                    const logoUrl = getImageUrl(image);
                                    return (
                                        <div
                                            key={`${key}-${brandHandle}-${i + 1}`}
                                            className={classNames([
                                                styles.section,
                                                styles[brandHandle],
                                            ])}
                                            data-brand={brandHandle}
                                        >
                                            <BrandLogo
                                                brand={brandHandle}
                                                className={styles.brandIcon}
                                            />
                                            {/* <img src={logoUrl} className={styles.brandIcon} /> */}
                                            <div className={styles.items}>
                                                {(brandItems || []).map(
                                                    ({ id, date: itemDate, ...props }) => (
                                                        <div
                                                            key={`item-${id}`}
                                                            className={styles.item}
                                                        >
                                                            <TimelineHorizontalCard
                                                                {...props}
                                                                height={50}
                                                                theme="rounded"
                                                                ratio="horizontal"
                                                                size="tiny"
                                                                brand={brand}
                                                                placeholder={
                                                                    isFetching && pages === null
                                                                }
                                                                className={styles.card}
                                                            />
                                                            <Duration className={styles.date}>
                                                                <FormattedTime value={itemDate} />
                                                            </Duration>
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </Accordion>
                        </div>
                    );
                })}
            </div>
            <div
                className={styles.end}
                ref={refEnd}
                style={{
                    height: 40,
                }}
            />
        </div>
    );
}

AccountTimelinePanel.propTypes = propTypes;
AccountTimelinePanel.defaultProps = defaultProps;

export default AccountTimelinePanel;
